import { groupBy, flatten } from 'lodash-es';
import { createIntl, createIntlCache } from 'react-intl';
import { IMedia, MediaType, Location, OtherDamageName } from '../types/media';
import messages from '../../assets/messages';
import renderLocale from '../helpers/renderLocale';

class Media implements IMedia {
  photos;

  videos;

  meta;

  constructor(data) {
    const multiMedia = Media.formatMultimedia(data?.media);

    this.photos = Media.formatPhotos(multiMedia.photos, data?.metadata);
    this.videos = Media.formatVideos(multiMedia.videos);
    this.meta = Media.getMultimediaMetaData(this.photos, this.videos);
  }

  /**
   * Returns multimedia shape
   */
  static formatMultimedia(multiMedia = []) {
    const formattedMultiMedia = {
      videos: [],
      photos: []
    };

    formattedMultiMedia.photos = multiMedia.filter(media => {
      return media.type === MediaType.image;
    });

    formattedMultiMedia.videos = multiMedia.filter(media => {
      return media.type === MediaType.video;
    });

    return formattedMultiMedia;
  }

  /**
   * Retrieve specific metadata by type i.e damage, image
   */
  static rtrvMetaDataByType(metaData, type: string) {
    const foundMetaData = metaData.find(elem => {
      return elem.type === MediaType.metadata[type];
    });

    return foundMetaData;
  }

  /**
   * Returns if the photo is passenger or back
   */
  static isBackPassengerSide(photo) {
    return (
      photo.imageLocation?.id === Location.passengerSide.id ||
      photo.imageLocation?.id === Location.back.id
    );
  }

  /**
   * Returns if the metadata is marked as Interior/Exterior damage
   */
  static isInterirorExteriorDamage(damageMetadata) {
    if (!damageMetadata?.data && Array.isArray(damageMetadata.data)) {
      return '';
    }

    const metaData = damageMetadata.data.find(data => {
      return data.key === 'displayName';
    });

    return !metaData ? '' : metaData.value;
  }

  /**
   * Returns a formatted photo
   */
  static formatPhotos(photos, metadata) {
    let damageIndex = 0;

    // set up internalization
    const locale = renderLocale();
    const intl = createIntl(
      { locale, messages: messages[locale] },
      createIntlCache()
    );

    const formattedPhotos = photos.map(photo => {
      // set defaults
      const formatPhoto: { [k: string]: any } = { ...photo };

      formatPhoto.uploadSuccess = true;
      formatPhoto.uploadError = false;

      // Fetch metadata
      const imageMetaData = Media.rtrvMetaDataByType(photo.metadata, 'image');
      const damageMetaData = Media.rtrvMetaDataByType(photo.metadata, 'damage');

      // Reformat damage photos
      if (damageMetaData?.data) {
        damageIndex += 1;

        formatPhoto.isDamage = true;
        formatPhoto.damageData = {
          index: damageIndex
        };
        // Image Location
        const damageMetadataImageLocationId = damageMetaData.data.find(data => {
          return data.key === 'imageLocationId';
        });

        // Damage Location Coordinates
        const x = damageMetaData.data.find(data => {
          return data.key === 'x';
        });

        const y = damageMetaData.data.find(data => {
          return data.key === 'y';
        });

        let damageDataImageLocation;

        if (
          damageMetadataImageLocationId &&
          damageMetadataImageLocationId.value &&
          metadata
        ) {
          damageDataImageLocation = metadata.imageLocationMetadata.find(
            data => {
              return (
                data.id === parseInt(damageMetadataImageLocationId.value, 10)
              );
            }
          );
        }

        if (damageDataImageLocation && x && y) {
          formatPhoto.damageData.imageLocation = damageDataImageLocation;
          formatPhoto.damageData.damageLocationCoordinates = {
            x: x.value,
            y: y.value
          };
        } else {
          // Set this damage photo's image location to "Other" or "Interior/Exterior Damage"
          const damgeTypeDisplayName = Media.isInterirorExteriorDamage(
            damageMetaData
          );

          formatPhoto.damageData.imageLocation = {
            id: -1,
            displayName:
              damgeTypeDisplayName === 'Interior Damage' ||
              damgeTypeDisplayName === 'Exterior Damage'
                ? damgeTypeDisplayName
                : intl.formatMessage({ id: 'vdp.multimedia.other' }),
            templates: []
          };
        }

        // Damage Location
        const damageMetadataLocation = damageMetaData.data.find(data => {
          return data.key === 'location';
        });

        if (damageMetadataLocation?.value && metadata) {
          formatPhoto.damageData.damageLocation = metadata.damageLocations.find(
            data => {
              return data.id === parseInt(damageMetadataLocation.value, 10);
            }
          );
        }

        // Damage Location Type
        const damageMetadataLocationType = damageMetaData.data.find(data => {
          return data.key === 'type';
        });

        if (
          formatPhoto.damageData?.damageLocation &&
          damageMetadataLocationType?.value
        ) {
          const damageType = formatPhoto.damageData.damageLocation.damageTypes.find(
            data => {
              return data.key === damageMetadataLocationType.value.toString();
            }
          );

          formatPhoto.damageData.damageType = damageType || {};
        }
      } else if (imageMetaData) {
        // Regular images (non-damage photos)
        formatPhoto.isDamage = false;

        // Image Location
        const imageMetadataLocationId = imageMetaData.data.find(data => {
          return data.key === 'imageLocationId';
        });

        let imageLocation;

        if (imageMetadataLocationId?.value) {
          imageLocation = metadata?.imageLocationMetadata.find(data => {
            return data.id === parseInt(imageMetadataLocationId.value, 10);
          });
        }

        if (imageLocation) {
          formatPhoto.imageLocation = imageLocation;
        } else {
          formatPhoto.imageLocation = {
            id: -1,
            displayName: intl.formatMessage({ id: 'vdp.multimedia.other' }),
            templates: []
          };
        }
      }

      return formatPhoto;
    });

    return formattedPhotos;
  }

  /**
   * TODO implement formatVideos
   */
  static formatVideos(videos) {
    return videos;
  }

  /**
   * Returns the trade glamour photo from a list of trade photos.
   */
  static getGlamourPhoto(photos) {
    const glamourPhoto = photos.find(photo => {
      return photo?.imageLocation?.displayName === Location.main.displayName;
    });

    if (glamourPhoto) {
      return glamourPhoto;
    }

    return photos?.length ? photos[0] : null;
  }

  /**
   * Add some extra information to the multimedia object to make it easier to retrieve
   * from the view. For example, find the glamour photo, secondary photos, and
   * photo/damages/video count.
   */
  static getMultimediaMetaData(photos, videos) {
    const meta = {
      count: {
        photos: 0,
        videos: 0,
        damages: 0
      },
      glamourPhoto: null,
      secondaryPhotos: [],
      photosMap: [],
      nonDamagePhotos: [],
      disclosurePhotos: []
    };

    Object.keys(Location).forEach(i => {
      if (Location[i].id !== Location.other.id) {
        meta.photosMap.push({ mediaId: Location[i].id });
      }
    });

    // meta.count = countBy(photos, photo => {
    //   return photo.isDamage ? 'damages' : 'photos';
    // });

    // meta.count.photos = meta.count.photos || 0;
    // meta.count.damages = meta.count.damages || 0;
    // meta.count.videos = videos ? videos.length : 0;

    // TODO remove hacky fix to include photos marked as exterior/interior
    // damage as disclosure images refer to AB151310
    photos.forEach(photo => {
      const photoRef = photo;
      if (photoRef.isDamage) {
        meta.count.damages += 1;
      } else {
        meta.count.photos += 1;
      }

      // add boolean field that identifies if photo is back or passenger side
      photoRef.isBackPassengerSide = Media.isBackPassengerSide(photoRef);
    });
    meta.count.videos = videos ? videos.length : 0;

    meta.glamourPhoto = Media.getGlamourPhoto(photos);
    meta.secondaryPhotos = photos.filter(
      photo =>
        photo.externalUrl !== meta.glamourPhoto.externalUrl && !photo.isDamage
    );

    // TODO remove hacky fix to include photos marked as exterior/interior
    // damage as disclosure images refer to AB151310
    const nonDamagePhotos = photos.filter(photo => {
      return photo?.imageLocation != null && !photo.isDamage;
    });

    const priorityMap = {};

    Object.keys(Location).forEach(elem => {
      const key = Location[elem].id;
      const { priority } = Location[elem];
      priorityMap[key] = { priority };
    });

    // sort through non damage photos and create a meta map arranged by photo
    // priority as defined by media Location const
    nonDamagePhotos
      .sort((a, b) => {
        if (
          priorityMap[a.imageLocation.id].priority <
          priorityMap[b.imageLocation.id].priority
        ) {
          return -1;
        }
        if (
          priorityMap[a.imageLocation.id].priority >
          priorityMap[b.imageLocation.id].priority
        ) {
          return 1;
        }

        return 0;
      })
      .forEach(photo => {
        if (photo.imageLocation.id !== Location.other.id) {
          const { priority } = priorityMap[photo.imageLocation.id];
          meta.photosMap[priority] = { ...photo, ...meta.photosMap[priority] };
        }
      });

    meta.nonDamagePhotos = nonDamagePhotos;

    const disclosurePhotos = photos.filter(photo => photo.isDamage);

    const groupedDisclosures = groupBy(disclosurePhotos, photo => {
      const damageId = photo?.damageData?.damageLocation?.id;
      const damageKey = photo?.damageData?.damageType?.key;

      return `${damageId}_${damageKey}`;
    });

    const formattedDisclosurePhotos = [];

    // set up internalization
    const locale = renderLocale();
    const intl = createIntl(
      { locale, messages: messages[locale] },
      createIntlCache()
    );

    Object.keys(groupedDisclosures).forEach(group => {
      formattedDisclosurePhotos.push(
        groupedDisclosures[group].map((photo, index, array) => {
          const photoCopy = photo;
          const displayName = photo?.damageData?.damageLocation?.displayName;
          const value = photo?.damageData?.damageType?.value;
          const otherImageName = photo?.damageData?.imageLocation?.displayName;

          if (
            otherImageName === OtherDamageName.interirorDamage ||
            otherImageName === OtherDamageName.exteriorDamage
          ) {
            if (otherImageName === OtherDamageName.interirorDamage) {
              photoCopy.displayName = intl.formatMessage({
                id: 'vdp.multimedia.interiordamage'
              });
            } else {
              photoCopy.displayName = intl.formatMessage({
                id: 'vdp.multimedia.exteriordamage'
              });
            }
          } else if (!displayName || !value) {
            // display nothing when displayName and value not set, scenario can occur on disclosure images
            // that were created on mobile clients and updated on web before patch occured AB#186357
            photoCopy.displayName = '';
          } else if (array.length === 1) {
            photoCopy.displayName = `${displayName} – ${value}`;
          } else {
            photoCopy.displayName = `${displayName} – ${value} ${index + 1}`;
          }

          return photo;
        })
      );
    });

    meta.disclosurePhotos = flatten(formattedDisclosurePhotos);

    // Map all damage photos associated to the nonDamagePhotos by imageLocationId
    meta.disclosurePhotos.forEach(disclosurePhoto => {
      if (disclosurePhoto?.damageData?.imageLocation?.id) {
        const matchingPhotoIdx = meta.nonDamagePhotos.findIndex(
          nonDamagePhoto =>
            nonDamagePhoto.imageLocation.id ===
            disclosurePhoto.damageData.imageLocation.id
        );
        if (matchingPhotoIdx !== -1) {
          const matchingPhoto = meta.nonDamagePhotos[matchingPhotoIdx];
          if (matchingPhoto?.damageMetaData?.length) {
            matchingPhoto.damageMetaData.push({
              id: btoa(disclosurePhoto.externalUrl),
              damageLocationCoordinates:
                disclosurePhoto.damageData.damageLocationCoordinates,
              displayName: disclosurePhoto.displayName,
              externalUrl: disclosurePhoto.externalUrl
            });
          } else {
            matchingPhoto.damageMetaData = [
              {
                id: btoa(disclosurePhoto.externalUrl),
                damageLocationCoordinates:
                  disclosurePhoto.damageData.damageLocationCoordinates,
                displayName: disclosurePhoto.displayName,
                externalUrl: disclosurePhoto.externalUrl
              }
            ];
          }
          meta.nonDamagePhotos[matchingPhotoIdx] = matchingPhoto;
        }
      }
    });

    return meta;
  }
}

export default Media;
